import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Upload, Download, Grid, List, Eye, DollarSign, Play, Calendar, Quote, Target, CheckCircle, FileText, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOpsDashboard } from '@/context/OpsDashboardContext';

interface Show {
  _id: string;
  show_name: string;
  artie_score: number;
}

interface Brand {
  _id: string;
  advertiser: string;
  competitive_categories: string[];
}

interface SlotDetail {
  slot_id: string;
  episode: string;
  slot_type: string;
  rate: number;
  integration_context: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  placement_duration?: string;
  placement_description?: string;
  estimated_impressions?: number;
  ecpm?: number;
  // Enhanced details from supply view
  opportunity_description?: string;
  script_quote?: string;
  integration_type?: string;
  show_name?: string;
  assigned_brand_campaign_id?: string;
  assigned_brand?: string;
  scene_location?: string;
  placement_examples?: string[];
  brand_suitability?: {
    recommended_categories: string[];
    placement_strength: string;
    visibility_level: string;
    integration_naturalness: string;
  };
}

interface AssignmentMapCell {
  show_id: string;
  brand_campaign_id: string;
  slot_count: number;
  has_conflict: boolean;
  total_rate?: number;
  average_rate?: number;
  slot_details?: SlotDetail[];
}

interface Conflict {
  show_id: string;
  show_name: string;
  conflicting_brands: Array<{
    brand_id: string;
    brand_name: string;
    competitive_category: string;
  }>;
}

interface AssignmentMapResponse {
  shows: Show[];
  brands: Brand[];
  assignments: AssignmentMapCell[];
  conflicts: Conflict[];
}

const AssignmentMapPage: React.FC = () => {
  const [data, setData] = useState<AssignmentMapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'matrix' | 'list'>('matrix');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<AssignmentMapCell | null>(null);
  const [isSlotDetailsOpen, setIsSlotDetailsOpen] = useState(false);
  const { toast } = useToast();
  const { refreshTriggers, refreshAfterSlotAssignment } = useOpsDashboard();

  const fetchAssignmentData = async () => {
    try {
      setLoading(true);
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/assignments/matrix`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assignment data');
      }

      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching assignment data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch assignment data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast({
        title: "Validation Error",
        description: "Please select a CSV or JSON file.",
        variant: "destructive"
      });
      return;
    }

    const allowedTypes = ['text/csv', 'application/json'];
    const hasValidExtension = /\.(csv|json)$/i.test(uploadFile.name);
    if (!allowedTypes.includes(uploadFile.type) && !hasValidExtension) {
      toast({
        title: "Validation Error",
        description: "Only CSV or JSON files are allowed.",
        variant: "destructive"
      });
      return;
    }

    const maxFileSizeBytes = 5 * 1024 * 1024;
    if (uploadFile.size > maxFileSizeBytes) {
      toast({
        title: "Validation Error",
        description: "File size must be 5MB or less.",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/assignments/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload assignments');
      }

      const result = await response.json();
      toast({
        title: "Success",
        description: result.message
      });

      setIsUploadDialogOpen(false);
      setUploadFile(null);
      fetchAssignmentData();
      
      // Trigger refresh for all tabs since slot assignments affect all views
      refreshAfterSlotAssignment();
    } catch (error) {
      console.error('Error uploading assignments:', error);
      toast({
        title: "Error",
        description: "Failed to upload assignments",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClearAssignments = async () => {
    if (!confirm('Are you sure you want to clear all slot assignments? This action cannot be undone.')) {
      return;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-dashboard/assignments/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: result.message,
        });
        // Refresh the data
        fetchAssignmentData();
        
        // Trigger refresh for all tabs since clearing assignments affects all views
        refreshAfterSlotAssignment();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to clear assignments",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear assignments",
        variant: "destructive",
      });
    }
  };

  const handleClearAssignment = async (slotId: string) => {
    if (!confirm('Are you sure you want to clear this slot assignment? The slot will remain but be unassigned.')) {
      return;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/ops-dashboard/assignments/clear-slot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slot_id: slotId
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: "Slot assignment cleared successfully",
        });
        // Refresh the data
        fetchAssignmentData();
        
        // Trigger refresh for all tabs since clearing assignments affects all views
        refreshAfterSlotAssignment();
        
        // Close the dialog if no more assigned slots
        if (selectedAssignment && selectedAssignment.slot_details.length === 1) {
          setIsSlotDetailsOpen(false);
          setSelectedAssignment(null);
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to clear assignment",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear assignment",
        variant: "destructive",
      });
    }
  };

  const getAssignmentCount = (showId: string, brandId: string): number => {
    const assignment = data?.assignments.find(
      a => a.show_id === showId && a.brand_campaign_id === brandId
    );
    return assignment?.slot_count || 0;
  };

  const hasConflict = (showId: string, brandId: string): boolean => {
    const assignment = data?.assignments.find(
      a => a.show_id === showId && a.brand_campaign_id === brandId
    );
    return assignment?.has_conflict || false;
  };

  const getShowTotalAssignments = (showId: string): number => {
    return data?.assignments
      .filter(a => a.show_id === showId)
      .reduce((sum, a) => sum + a.slot_count, 0) || 0;
  };

  const getBrandTotalAssignments = (brandId: string): number => {
    return data?.assignments
      .filter(a => a.brand_campaign_id === brandId)
      .reduce((sum, a) => sum + a.slot_count, 0) || 0;
  };

  const getAssignmentDetails = (showId: string, brandId: string): AssignmentMapCell | null => {
    return data?.assignments.find(
      a => a.show_id === showId && a.brand_campaign_id === brandId
    ) || null;
  };

  const handleCellClick = (showId: string, brandId: string) => {
    const assignment = getAssignmentDetails(showId, brandId);
    if (assignment && assignment.slot_count > 0) {
      setSelectedAssignment(assignment);
      setIsSlotDetailsOpen(true);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleExport = () => {
    if (!data) return;

    const headers = [
      'Show',
      'Brand',
      'Slot Count',
      'Has Conflict',
      'Total Rate',
      'Average Rate'
    ];

    const rows = data.assignments.map((assignment) => {
      const show = data.shows.find((s) => s._id === assignment.show_id);
      const brand = data.brands.find((b) => b._id === assignment.brand_campaign_id);

      return [
        show?.show_name || assignment.show_id,
        brand?.advertiser || assignment.brand_campaign_id,
        assignment.slot_count.toString(),
        assignment.has_conflict ? 'Yes' : 'No',
        (assignment.total_rate ?? 0).toString(),
        (assignment.average_rate ?? 0).toString()
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((field) => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
              return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          })
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'assignment-map.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchAssignmentData();
  }, []);

  // Listen for refresh triggers from the context
  useEffect(() => {
    if (refreshTriggers.assignments > 0) {
      fetchAssignmentData();
    }
  }, [refreshTriggers.assignments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No assignment data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignment Map</h1>
          <p className="text-gray-600">Visual cross-reference of show and brand assignments</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'matrix' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('matrix')}
            >
              <Grid className="h-4 w-4 mr-2" />
              Matrix
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-2" />
              List
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!data.assignments.length}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAssignments}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            Clear All
          </Button>
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Upload Assignments
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Package Assignments</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="file">Select CSV or JSON file</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".csv,.json"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    CSV format: brand_campaign_id, show_id, slot_id, rate
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsUploadDialogOpen(false);
                      setUploadFile(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFileUpload}
                    disabled={!uploadFile || uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Conflicts Alert */}
      {data.conflicts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Competitive Conflicts Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.conflicts.map((conflict, index) => (
                <div key={index} className="p-3 bg-white rounded border border-red-200">
                  <div className="font-medium text-red-900">{conflict.show_name}</div>
                  <div className="text-sm text-red-700">
                    Conflicting brands: {conflict.conflicting_brands.map(b => b.brand_name).join(', ')}
                  </div>
                  <div className="text-xs text-red-600">
                    Shared category: {conflict.conflicting_brands[0]?.competitive_category}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment Matrix/List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {viewMode === 'matrix' ? 'Assignment Matrix' : 'Assignment List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === 'matrix' ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">
                      Show / Brand
                    </TableHead>
                    {data.brands.map((brand) => (
                      <TableHead key={brand._id} className="text-center min-w-[120px]">
                        <div className="space-y-1">
                          <div className="font-medium text-xs">{brand.advertiser}</div>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {brand.competitive_categories.slice(0, 2).map((cat, index) => (
                              <Badge key={index} variant="outline" className="text-xs px-1 py-0">
                                {cat.split('/')[0]}
                              </Badge>
                            ))}
                          </div>
                          <div className="text-xs text-gray-500">
                            Total: {getBrandTotalAssignments(brand._id)}
                          </div>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-center">Row Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.shows.map((show) => (
                    <TableRow key={show._id}>
                      <TableCell className="sticky left-0 bg-white z-10">
                        <div className="space-y-1">
                          <div className="font-medium text-sm">{show.show_name}</div>
                          <Badge variant="outline" className="text-xs">
                            Score: {show.artie_score.toFixed(1)}
                          </Badge>
                        </div>
                      </TableCell>
                      {data.brands.map((brand) => {
                        const count = getAssignmentCount(show._id, brand._id);
                        const conflict = hasConflict(show._id, brand._id);
                        const assignment = getAssignmentDetails(show._id, brand._id);
                        return (
                          <TableCell key={brand._id} className="text-center">
                            {count > 0 && (
                              <div
                                className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                                onClick={() => handleCellClick(show._id, brand._id)}
                              >
                                <Badge
                                  variant={conflict ? "destructive" : "default"}
                                  className={`${conflict ? "bg-red-100 text-red-800 border-red-300" : ""} hover:opacity-80`}
                                >
                                  {count}
                                  {conflict && <AlertTriangle className="h-3 w-3 ml-1" />}
                                  <Eye className="h-3 w-3 ml-1" />
                                </Badge>
                                {assignment?.total_rate && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {formatCurrency(assignment.total_rate)}
                                  </div>
                                )}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center">
                        <Badge variant="outline">
                          {getShowTotalAssignments(show._id)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Column Totals Row */}
                  <TableRow className="bg-gray-50">
                    <TableCell className="sticky left-0 bg-gray-50 z-10 font-medium">
                      Column Total
                    </TableCell>
                    {data.brands.map((brand) => (
                      <TableCell key={brand._id} className="text-center">
                        <Badge variant="outline">
                          {getBrandTotalAssignments(brand._id)}
                        </Badge>
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      <Badge variant="default">
                        {data.assignments.reduce((sum, a) => sum + a.slot_count, 0)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            /* List View */
            <div className="space-y-6">
              {/* Shows List */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Shows ({data.shows.length})</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Show Name</TableHead>
                        <TableHead>Artie Score</TableHead>
                        <TableHead>Total Assignments</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.shows.length > 0 ? (
                        data.shows.map((show) => {
                          const totalAssignments = getShowTotalAssignments(show._id);
                          return (
                            <TableRow key={show._id}>
                              <TableCell className="font-medium">{show.show_name}</TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {show.artie_score.toFixed(1)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="default">
                                  {totalAssignments}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={totalAssignments > 0 ? "default" : "secondary"}>
                                  {totalAssignments > 0 ? "Assigned" : "Available"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                            No shows available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Brands List */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Brands ({data.brands.length})</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Advertiser</TableHead>
                        <TableHead>Competitive Categories</TableHead>
                        <TableHead>Total Assignments</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.brands.length > 0 ? (
                        data.brands.map((brand) => {
                          const totalAssignments = getBrandTotalAssignments(brand._id);
                          return (
                            <TableRow key={brand._id}>
                              <TableCell className="font-medium">{brand.advertiser}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {brand.competitive_categories.length > 0 ? (
                                    brand.competitive_categories.map((cat, catIndex) => (
                                      <Badge key={catIndex} variant="secondary" className="text-xs">
                                        {cat}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-gray-400 text-sm">No categories</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="default">
                                  {totalAssignments}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={totalAssignments > 0 ? "default" : "secondary"}>
                                  {totalAssignments > 0 ? "Active" : "Available"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                            No brands available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Assignments List (if any) */}
              {data.assignments.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Active Assignments ({data.assignments.length})</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Show</TableHead>
                          <TableHead>Brand</TableHead>
                          <TableHead>Slot Count</TableHead>
                          <TableHead>Total Rate</TableHead>
                          <TableHead>Avg Rate</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.assignments.map((assignment, index) => {
                          const show = data.shows.find(s => s._id === assignment.show_id);
                          const brand = data.brands.find(b => b._id === assignment.brand_campaign_id);
                          
                          return (
                            <TableRow key={index}>
                              <TableCell className="font-medium">
                                {show?.show_name || 'Unknown Show'}
                              </TableCell>
                              <TableCell>
                                {brand?.advertiser || 'Unknown Brand'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="default">
                                  {assignment.slot_count}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {assignment.total_rate ? (
                                  <span className="font-semibold text-green-600">
                                    {formatCurrency(assignment.total_rate)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {assignment.average_rate ? (
                                  <span className="font-medium">
                                    {formatCurrency(assignment.average_rate)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {assignment.has_conflict ? (
                                  <Badge variant="destructive" className="bg-red-100 text-red-800">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Conflict
                                  </Badge>
                                ) : (
                                  <Badge variant="default" className="bg-green-100 text-green-800">
                                    Clear
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAssignment(assignment);
                                    setIsSlotDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Shows</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.shows.length}</div>
            <p className="text-xs text-gray-500">With assignments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Brands</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.brands.length}</div>
            <p className="text-xs text-gray-500">With packages</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.assignments.reduce((sum, a) => sum + a.slot_count, 0)}
            </div>
            <p className="text-xs text-gray-500">Slot assignments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Conflicts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{data.conflicts.length}</div>
            <p className="text-xs text-gray-500">Competitive conflicts</p>
          </CardContent>
        </Card>
      </div>

      {/* Slot Details Dialog */}
      <Dialog open={isSlotDetailsOpen} onOpenChange={setIsSlotDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Slot Assignment Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-6">
              {/* Assignment Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-600">Show</div>
                  <div className="text-lg font-semibold">
                    {data?.shows.find(s => s._id === selectedAssignment.show_id)?.show_name || 'Unknown Show'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600">Brand</div>
                  <div className="text-lg font-semibold">
                    {data?.brands.find(b => b._id === selectedAssignment.brand_campaign_id)?.advertiser || 'Unknown Brand'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600">Status</div>
                  <div className="flex items-center gap-2">
                    {selectedAssignment.has_conflict ? (
                      <Badge variant="destructive" className="bg-red-100 text-red-800">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Conflict
                      </Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        Clear
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              {selectedAssignment.total_rate && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        Total Rate
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(selectedAssignment.total_rate)}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Average Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {selectedAssignment.average_rate ? formatCurrency(selectedAssignment.average_rate) : 'N/A'}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600">Slot Count</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedAssignment.slot_count}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Slot Details */}
              {selectedAssignment.slot_details && selectedAssignment.slot_details.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Placement Opportunities ({selectedAssignment.slot_details.length})</h3>
                  <div className="space-y-4">
                    {selectedAssignment.slot_details.map((slot, index) => (
                      <Card key={index} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left Column - Basic Info */}
                            <div className="space-y-4">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Play className="h-4 w-4 text-blue-600" />
                                    <span className="font-semibold text-gray-900">Slot #{index + 1}</span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleClearAssignment(slot.slot_id)}
                                    className="text-red-600 border-red-200 hover:bg-red-50 h-8 w-8 p-0"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="text-xs font-mono text-gray-500 bg-gray-100 p-2 rounded">
                                  {slot.slot_id}
                                </div>
                              </div>
                              
                              <div>
                                <div className="text-sm font-medium text-gray-600 mb-1">Episode</div>
                                <Badge variant="outline" className="text-sm">
                                  {slot.episode}
                                </Badge>
                              </div>
                              
                              <div>
                                <div className="text-sm font-medium text-gray-600 mb-1">Placement Type</div>
                                <Badge variant={slot.slot_type === 'narrative_integration' ? 'default' : 'secondary'} className="text-sm">
                                  {slot.slot_type?.replace('_', ' ') || 'Unknown'}
                                </Badge>
                              </div>
                              
                              {slot.status && (
                                <div>
                                  <div className="text-sm font-medium text-gray-600 mb-1">Status</div>
                                  <Badge variant="outline" className="text-sm capitalize">
                                    {slot.status}
                                  </Badge>
                                </div>
                              )}
                            </div>

                            {/* Middle Column - Placement Details */}
                            <div className="space-y-4">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <DollarSign className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium text-gray-600">Rate (eCPM)</span>
                                </div>
                                <div className="text-lg font-bold text-green-600">
                                  {formatCurrency(slot.rate)}
                                </div>
                              </div>
                              
                              {slot.created_at && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <Calendar className="h-4 w-4 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-600">Created</span>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {new Date(slot.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right Column - Enhanced Placement Details */}
                            <div className="space-y-4">
                              {/* Opportunity Description */}
                              {slot.opportunity_description && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <Target className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium text-gray-600">Opportunity</span>
                                  </div>
                                  <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                    {slot.opportunity_description}
                                  </div>
                                </div>
                              )}
                              
                              {/* Script Quote */}
                              {slot.script_quote && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <FileText className="h-4 w-4 text-purple-600" />
                                    <span className="text-sm font-medium text-gray-600">Script Context</span>
                                  </div>
                                  <div className="text-sm text-gray-700 bg-purple-50 p-3 rounded-lg border border-purple-200 italic">
                                    "{slot.script_quote}"
                                  </div>
                                </div>
                              )}
                              
                              {/* Integration Type & Scene Location */}
                              <div className="grid grid-cols-1 gap-3">
                                {slot.integration_type && (
                                  <div>
                                    <div className="text-sm font-medium text-gray-600 mb-1">Integration Type</div>
                                    <Badge variant="outline" className="text-sm bg-orange-50 text-orange-700 border-orange-200">
                                      {slot.integration_type}
                                    </Badge>
                                  </div>
                                )}
                                
                                {slot.scene_location && (
                                  <div>
                                    <div className="text-sm font-medium text-gray-600 mb-1">Scene Location</div>
                                    <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded border">
                                      {slot.scene_location}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* No slot details message */}
              {(!selectedAssignment.slot_details || selectedAssignment.slot_details.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No detailed slot information available for this assignment.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentMapPage;